import { FC, memo, useCallback, useState } from 'react'

import { useTranslation } from '@crew/modules/i18n'

import { CrewScrollView } from 'components/devextreme/crewScrollView'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { TaskTemplateListPanel } from './components/taskTemplateListPanel/taskTemplateListPanel'
import { TaskTemplateTaskPanel } from './components/taskTemplateTaskPanel/taskTemplateTaskPanel'
import { useTaskEntryByTemplateForm } from './useTaskEntryByTemplateForm'
import { useValueChangeEffect } from '@crew/hooks'

export type TaskEntryByTemplateFormProps = {
  projectId: string
  isOpen: boolean
  onSubmit: () => void
  onCancel: () => void
}

// 「テンプレートからタスク登録」フォーム
export const TaskEntryByTemplateForm: FC<TaskEntryByTemplateFormProps> = memo(
  (props) => {
    const { t } = useTranslation()

    const {
      control,
      setValue,
      getValues,
      templates,
      templateTasks,
      selectedTemplateId,
      setSelectedTemplateId,
      mappingRequiredAssignToUsers,
      mappingRequiredTaskKinds,
      userDataSource,
      taskKindDataSource,
      insertTasksByTemplate,
      loadTaskTemplates,
      loadTaskTemplateTasks,
      canRegister,
      hasMappingError,
      isRegistering,
    } = useTaskEntryByTemplateForm(
      props.projectId,
      props.isOpen,
      props.onSubmit
    )

    // テンプレート一覧のソート条件
    const [sort, setSort] = useState<string[]>([])

    // 登録ボタンクリック時の処理
    const handleSubmitButtonClick = useCallback(() => {
      insertTasksByTemplate()
    }, [insertTasksByTemplate])

    // キャンセルボタンクリック時の処理
    const handleCancelButtonClick = useCallback(() => {
      props.onCancel()
    }, [props])

    // 検索キーワード変更時の処理
    const handleSearchKeywordChange = useCallback(
      (value: string) => {
        setValue('searchKeyword', value ?? '')
        loadTaskTemplates(value, sort)
      },
      [setValue, loadTaskTemplates, sort]
    )

    // ソート条件変更時、テンプレート一覧を再取得
    useValueChangeEffect(
      () => {
        // 検索キーワードを取得
        const searchKeyword = getValues('searchKeyword') ?? ''

        // テンプレート一覧を再取得
        loadTaskTemplates(searchKeyword, sort)
      },
      [getValues, loadTaskTemplates, sort],
      sort
    )

    // テンプレート選択時の処理
    const handleSelectTemplate = useCallback(
      (templateId: string) => {
        setSelectedTemplateId(templateId)
        loadTaskTemplateTasks(templateId)
      },
      [loadTaskTemplateTasks, setSelectedTemplateId]
    )

    return (
      // モーダルの最小幅を制限し、画面の各入力項目が正しく表示されるようにする
      <div className="h-full flex flex-col gap-y-2.5">
        <CrewScrollView>
          {/* テンプレート一覧とテンプレートタスク一覧は1.3:2の幅で表示するため、flex-[1.3]とflex-[2]を指定している */}
          <div className="flex flex-row gap-x-2">
            {/* テンプレート一覧 */}
            <div className="flex-[1.3] min-w-0 pr-2 border-r">
              <TaskTemplateListPanel
                control={control}
                templates={templates}
                selectedTemplateId={selectedTemplateId}
                onSelectTemplate={handleSelectTemplate}
                onSearchKeywordChange={handleSearchKeywordChange}
                sort={sort}
                setSort={setSort}
              />
            </div>
            {/* テンプレートタスク一覧 */}
            <div className="flex-[2] min-w-0">
              <TaskTemplateTaskPanel
                control={control}
                tasks={templateTasks}
                mappingRequiredAssignToUsers={mappingRequiredAssignToUsers}
                mappingRequiredTaskKinds={mappingRequiredTaskKinds}
                userDataSource={userDataSource}
                taskKindDataSource={taskKindDataSource}
                hasMappingError={hasMappingError}
                isTemplateUnselected={!selectedTemplateId}
              />
            </div>
          </div>
        </CrewScrollView>
        <div className="flex flex-row items-center gap-x-2.5 justify-end">
          {/* 登録 */}
          <CrewButton
            id="task-entry-by-template-dialog-submit-button"
            text={t('action.register')}
            type="primary"
            onClick={handleSubmitButtonClick}
            disabled={!canRegister || isRegistering}
          />

          {/* キャンセル */}
          <CrewButton
            id="task-entry-by-template-dialog-cancel-button"
            text={t('action.cancel')}
            type="normal"
            stylingMode="outlined"
            onClick={handleCancelButtonClick}
            disabled={isRegistering}
          />
        </div>
      </div>
    )
  }
)
